import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { AppError } from "@/jenesys-types/errors";
import {
  ListBaselineSorts,
  ListLocalStorageConfig,
  ListSearchCriteria,
  ListSearchOperator,
  ListSortCriteria
} from "@/jenesys-types/list";

import { Params, PathParams } from "@/services/api";
import { selectCombinedListData } from "@/store/crud";
import {
  createList,
  filterByStatus as filterByStatusAction,
  loadList as loadListAction,
  loadListWithoutClearing as loadListWithoutClearingAction,
  loadNextListPage as loadNextListPageAction,
  loadSpecificListPage as LoadSpecificListPageAction,
  refreshList as refreshListAction,
  removeList,
  searchList as searchListAction,
  sortList as sortListAction
} from "@/store/crud/list";
import { performanceMonitor } from "@/utils/performance";

export interface UseEntityListOptions {
  actionName?: string;
  initialFilterCriteria?: ListSearchCriteria;
  initialSearchCriteria?: ListSearchCriteria;
  initialSortCriteria?: ListSortCriteria;
  loadOnMount?: boolean;
  pathParams?: PathParams;
  queryParams?: Params;
  relationships?: Array<string>;
  searchOperator?: ListSearchOperator;
  statusCounts?: Record<string, number>;
  useSimpleSearch?: boolean;
  baselineSorts?: ListBaselineSorts;
  localStorageConfig?: ListLocalStorageConfig;
}

const defaultOptions: UseEntityListOptions = {};

export interface UseEntityListReturn<E extends Entity> {
  allEntities: Array<E>;
  currentPage: number;
  currentPageEntities: Array<E>;
  error?: AppError;
  filterCriteria?: ListSearchCriteria;
  hasMore: boolean;
  isLoading: boolean;
  isManualLoading: boolean;
  knownPageCount: number;
  loadList: () => void;
  loadListForPolling: () => void;
  loadNextListPage: () => void;
  loadSpecificListPage: (pageIndex: number) => void;
  refreshList: () => void;
  searchCriteria?: ListSearchCriteria;
  searchList: (searchCriteria?: ListSearchCriteria) => void;
  sortCriteria?: ListSortCriteria;
  filterListByStatus: (status: string | string[]) => void;
  statusesFilter?: string[];
  sortList: (sortCriteria?: ListSortCriteria) => void;
  statusCounts?: Record<string, number>;
  lastChosenStatus?: string;
  total: number;
  offset: number;
  limit: number;
}

const useEntityList = <E extends Entity = Entity>(
  dataType: DataType,
  listId: string,
  options: UseEntityListOptions = defaultOptions
): UseEntityListReturn<E> => {
  const dispatch = useDispatch();
  const [isManualLoading, setIsManualLoading] = useState(false);

  // Track previous loading state to detect changes
  const wasLoadingRef = useRef(false);

  const hookInstanceId = useMemo(
    () =>
      `${dataType}-${listId}-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 7)}`,
    [dataType, listId]
  );

  // Memoize the selector otherwise it will be new function per render.
  const combinedListDataSelector = useMemo(
    () => selectCombinedListData<E>(dataType, listId),
    [dataType, listId]
  );
  const listData = useSelector(combinedListDataSelector);

  // Update manual loading flag when loading state changes
  useEffect(() => {
    if (wasLoadingRef.current && !listData.isLoading) {
      // Loading has completed, reset manual loading flag
      setIsManualLoading(false);
    }
    wasLoadingRef.current = listData.isLoading;
  }, [listData.isLoading]);

  // Regular load that clears existing data - used for manual refreshes
  const loadList = useCallback(() => {
    setIsManualLoading(true);
    dispatch(loadListAction(dataType, listId));
  }, [dataType, dispatch, listId]);

  // Special load for polling that doesn't clear existing data
  const loadListForPolling = useCallback(() => {
    dispatch(loadListWithoutClearingAction(dataType, listId));
  }, [dataType, dispatch, listId]);

  const loadNextListPage = useCallback(() => {
    setIsManualLoading(true);
    dispatch(loadNextListPageAction(dataType, listId));
  }, [dataType, dispatch, listId]);

  const loadSpecificListPage = useCallback(
    (pageNumber: number) => {
      setIsManualLoading(true);
      dispatch(LoadSpecificListPageAction(dataType, listId, pageNumber - 1));
    },
    [dataType, dispatch, listId]
  );

  const refreshList = useCallback(() => {
    setIsManualLoading(true);
    dispatch(refreshListAction(dataType, listId));
  }, [dataType, dispatch, listId]);

  const searchList = useCallback(
    (newSearchCriteria?: ListSearchCriteria) => {
      setIsManualLoading(true);
      dispatch(searchListAction(dataType, listId, newSearchCriteria));
    },
    [dataType, dispatch, listId]
  );

  const filterListByStatus = useCallback(
    (status: string | string[]) => {
      dispatch(filterByStatusAction(dataType, listId, status));
    },
    [dataType, dispatch, listId]
  );

  const sortList = useCallback(
    (newSortCriteria?: ListSortCriteria) => {
      setIsManualLoading(true);
      dispatch(sortListAction(dataType, listId, newSortCriteria));
    },
    [dispatch, dataType, listId]
  );

  const totalPages = useMemo(() => {
    if (listData.totalEntityCount && listData.limit) {
      return Math.ceil(listData.totalEntityCount / listData.limit);
    }
    return 0;
  }, [listData.totalEntityCount, listData.limit]);

  const hasMore = useMemo(() => {
    if (
      listData.offset !== undefined &&
      listData.totalEntityCount !== undefined
    ) {
      return listData.offset + listData.limit < listData.totalEntityCount;
    }
    return false;
  }, [listData.offset, listData.totalEntityCount, listData.limit]);

  const {
    actionName,
    initialFilterCriteria,
    initialSearchCriteria,
    initialSortCriteria,
    loadOnMount = true,
    pathParams,
    queryParams,
    relationships,
    searchOperator,
    baselineSorts,
    localStorageConfig
  } = options;
  useEffect(() => {
    const metricName = "Hook.useEntityList.initialization";
    const initialContext = {
      hookInstanceId,
      effectInstanceId: `${hookInstanceId}-init-${performance.now()}`,
      dataType,
      listId,
      actionName,
      loadOnMount,
      initialSearchCriteria,
      initialFilterCriteria,
      initialSortCriteria,
      useSimpleSearch: options.useSimpleSearch,
      pathParams,
      queryParams,
      relationships,
      searchOperator,
      baselineSorts,
      localStorage
    };
    const metricId = performanceMonitor.start(metricName, initialContext);

    dispatch(
      createList(
        dataType,
        listId,
        actionName,
        pathParams,
        queryParams,
        relationships,
        initialSearchCriteria,
        initialFilterCriteria,
        searchOperator,
        initialSortCriteria,
        options.statusCounts,
        options.useSimpleSearch,
        baselineSorts,
        localStorageConfig
      )
    );
    if (loadOnMount) {
      loadList(); // This is already instrumented
    }
    performanceMonitor.stop(metricId); // Stop the initialization metric itself

    return () => {
      // No specific metric for cleanup, but could be added if needed.
      dispatch(removeList(dataType, listId));
    };
  }, [
    actionName,
    dataType,
    dispatch,
    initialFilterCriteria,
    initialSearchCriteria,
    initialSortCriteria,
    listId,
    loadList,
    loadOnMount,
    pathParams,
    queryParams,
    relationships,
    searchOperator,
    options.statusCounts,
    options.useSimpleSearch,
    baselineSorts,
    hookInstanceId,
    localStorageConfig
  ]);

  return {
    allEntities: listData.allEntities,
    currentPage: listData.currentPageIndex + 1,
    currentPageEntities: listData.currentPageEntities,
    error: listData.error,
    filterCriteria: listData.filterCriteria,
    hasMore,
    isLoading: listData.isLoading,
    isManualLoading,
    knownPageCount: totalPages,
    loadList,
    loadListForPolling,
    loadNextListPage,
    loadSpecificListPage,
    refreshList,
    searchCriteria: listData.searchCriteria,
    searchList,
    filterListByStatus,
    statusesFilter: listData.statusesFilter,
    sortCriteria: listData.sortCriteria,
    sortList,
    statusCounts: listData.statusCounts,
    lastChosenStatus: listData.lastChosenStatus,
    total: listData.totalEntityCount,
    offset: listData.offset,
    limit: listData.limit
  };
};

export default useEntityList;
